import { auth } from "./auth-client";

/**
 * OneSignal notification helper utilities
 * Centralizes notification sending logic with standardized error handling
 */

export interface BaseNotificationPayload {
  notification_type: string;
  study_group_id?: string;
  [key: string]: any;
}

export interface SessionSharedNotification extends BaseNotificationPayload {
  notification_type: "shared";
  study_group_id: string;
  practice_session_id: string;
  shared_by: string;
  session_slug: string;
  session_topic: string;
  group_name: string;
  sharer_email: string;
  message?: string | null;
}

export interface MemberJoinedNotification extends BaseNotificationPayload {
  notification_type: "member_joined";
  study_group_id: string;
  group_name: string;
  user_id: string;
  user_email: string;
}

export interface MemberLeftNotification extends BaseNotificationPayload {
  notification_type: "member_left";
  study_group_id: string;
  group_name: string;
  user_id: string;
  user_email: string;
}

export interface SessionDeletedNotification extends BaseNotificationPayload {
  notification_type: "deleted";
  study_group_id: string;
  practice_session_id: string;
  shared_by: string;
  session_slug: string;
  session_topic: string;
  group_name: string;
  sharer_email: string;
  deleted_by: string;
  deleter_email: string;
  is_deleter_sharer: boolean;
}

export interface GroupTopicOfDayNotification extends BaseNotificationPayload {
  notification_type: "group_topic_of_day";
  study_group_id: string;
  practice_session_id: string;
  session_slug: string;
  session_topic: string;
  group_name: string;
  set_by: string;
  set_by_email: string;
}

export interface SessionCommentToOwnerNotification extends BaseNotificationPayload {
  notification_type: "session_comment_to_owner";
  comment_id: string;
}

export interface SessionCommentReplyToCommentOwnerNotification extends BaseNotificationPayload {
  notification_type: "session_comment_reply_to_comment_owner";
  reply_comment_id: string;
}

export interface CollectionSharedNotification extends BaseNotificationPayload {
  notification_type: "collection_shared";
  study_group_id: string;
  collection_id: string;
  shared_by: string;
  message?: string | null;
}

export type NotificationPayload =
  | SessionSharedNotification
  | MemberJoinedNotification
  | MemberLeftNotification
  | SessionDeletedNotification
  | GroupTopicOfDayNotification
  | SessionCommentToOwnerNotification
  | SessionCommentReplyToCommentOwnerNotification
  | CollectionSharedNotification
  | BaseNotificationPayload;

/**
 * Send a OneSignal notification via Edge Function
 * Handles authentication, error handling, and silent failures
 * 
 * @param payload Notification payload
 * @param options Optional configuration
 * @returns Promise<boolean> True if successful, false otherwise (never throws)
 */
export async function sendOneSignalNotification(
  payload: NotificationPayload,
  options: { throwOnError?: boolean } = {}
): Promise<boolean> {
  const { throwOnError = false } = options;

  try {
    // The API owns recipient resolution and the OneSignal key; this only names
    // the event.
    const apiBase = ((import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "").replace(/\/+$/, "");
    if (!apiBase) return false;

    const {
      data: { session: authSession },
    } = await auth.getSession();

    if (!authSession?.access_token) {
      console.warn("No auth session available for OneSignal notification");
      if (throwOnError) {
        throw new Error("Not authenticated");
      }
      return false;
    }

    // Send notification request
    const response = await fetch(`${apiBase}/v1/notifications/push`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authSession.access_token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => "Unknown error");
      console.warn("[notification] OneSignal API error", {
        notification_type: payload.notification_type,
        status: response.status,
        error: errorText,
        timestamp: new Date().toISOString(),
      });
      if (throwOnError) {
        throw new Error(`OneSignal API error: ${response.status} - ${errorText}`);
      }
      return false;
    }

    return true;
  } catch (error) {
    // Log with structured context for observability
    console.warn("[notification] OneSignal notification failed", {
      notification_type: payload.notification_type,
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      timestamp: new Date().toISOString(),
    });
    if (throwOnError) {
      throw error;
    }
    return false;
  }
}

/**
 * Send a notification when a session is shared to a group
 */
export async function sendSessionSharedNotification(params: {
  groupId: string;
  sessionId: string;
  sharedBy: string;
  sessionSlug: string;
  sessionTopic: string;
  groupName: string;
  sharerEmail: string;
  message?: string | null;
}): Promise<boolean> {
  return sendOneSignalNotification({
    notification_type: "shared",
    study_group_id: params.groupId,
    practice_session_id: params.sessionId,
    shared_by: params.sharedBy,
    session_slug: params.sessionSlug,
    session_topic: params.sessionTopic,
    group_name: params.groupName,
    sharer_email: params.sharerEmail,
    message: params.message || null,
  });
}

/**
 * Send a notification when a member joins a group
 */
export async function sendMemberJoinedNotification(params: {
  groupId: string;
  groupName: string;
  userId: string;
  userEmail: string;
}): Promise<boolean> {
  return sendOneSignalNotification({
    notification_type: "member_joined",
    study_group_id: params.groupId,
    group_name: params.groupName,
    user_id: params.userId,
    user_email: params.userEmail,
  });
}

/**
 * Send a notification when a member leaves a group
 */
export async function sendMemberLeftNotification(params: {
  groupId: string;
  groupName: string;
  userId: string;
  userEmail: string;
}): Promise<boolean> {
  return sendOneSignalNotification({
    notification_type: "member_left",
    study_group_id: params.groupId,
    group_name: params.groupName,
    user_id: params.userId,
    user_email: params.userEmail,
  });
}

/**
 * Send a notification when a shared session is deleted
 */
export async function sendSessionDeletedNotification(params: {
  groupId: string;
  sessionId: string;
  sharedBy: string;
  sessionSlug: string;
  sessionTopic: string;
  groupName: string;
  sharerEmail: string;
  deletedBy: string;
  deleterEmail: string;
  isDeleterSharer: boolean;
}): Promise<boolean> {
  return sendOneSignalNotification({
    notification_type: "deleted",
    study_group_id: params.groupId,
    practice_session_id: params.sessionId,
    shared_by: params.sharedBy,
    session_slug: params.sessionSlug,
    session_topic: params.sessionTopic,
    group_name: params.groupName,
    sharer_email: params.sharerEmail,
    deleted_by: params.deletedBy,
    deleter_email: params.deleterEmail,
    is_deleter_sharer: params.isDeleterSharer,
  });
}

/**
 * Send a notification when a collection is shared to a group
 */
export async function sendCollectionSharedNotification(params: {
  groupId: string;
  collectionId: string;
  sharedBy: string;
  message?: string | null;
}): Promise<boolean> {
  // A collection is not a session: sending it as `shared` failed validation
  // (no practice_session_id) and would have linked the tap to `/session/`.
  return sendOneSignalNotification({
    notification_type: "collection_shared",
    study_group_id: params.groupId,
    collection_id: params.collectionId,
    shared_by: params.sharedBy,
    message: params.message || null,
  });
}

/**
 * Send a notification when group topic of day is set
 */
export async function sendGroupTopicOfDayNotification(params: {
  groupId: string;
  sessionId: string;
  sessionSlug: string;
  sessionTopic: string;
  groupName: string;
  setBy: string;
  setByEmail: string;
}): Promise<boolean> {
  return sendOneSignalNotification({
    notification_type: "group_topic_of_day",
    study_group_id: params.groupId,
    practice_session_id: params.sessionId,
    session_slug: params.sessionSlug,
    session_topic: params.sessionTopic,
    group_name: params.groupName,
    set_by: params.setBy,
    set_by_email: params.setByEmail,
  });
}

// ============================================
// Session Comment Notifications
// ============================================

/**
 * Send a notification to session owner when someone comments on their session
 */
export async function sendSessionCommentToOwnerNotification(params: {
  commentId: string;
}): Promise<boolean> {
  return sendOneSignalNotification({
    notification_type: "session_comment_to_owner",
    comment_id: params.commentId,
  });
}

export async function sendSessionCommentReplyToCommentOwnerNotification(params: {
  replyCommentId: string;
}): Promise<boolean> {
  return sendOneSignalNotification({
    notification_type: "session_comment_reply_to_comment_owner",
    reply_comment_id: params.replyCommentId,
  });
}

